import React from 'react';
import { cn } from '../lib/utils';
import { useLanguage } from '../contexts/LanguageContext';
import { Monitor } from 'lucide-react';

export default function DeskGrid({ 
  desks, 
  bookings, 
  selectedDesk, 
  onDeskClick, 
  isAdmin,
  selectedDates 
}) {
  const { t } = useLanguage();

  const getBookingForDesk = (deskId) => {
    return bookings.find(b => b.desk_id === deskId);
  };

  const isWeekdayBlocked = (desk) => {
    const allowed = desk.available_weekdays || [0, 1, 2, 3, 4];
    return (selectedDates || []).some(d => !allowed.includes((d.getDay() + 6) % 7));
  };

  const getDeskStatus = (desk) => {
    if (!desk.is_available) return 'unavailable';
    if (isWeekdayBlocked(desk)) return 'unavailable';
    
    const booking = getBookingForDesk(desk.desk_id);
    if (booking) {
      if (booking.is_mine) return 'my-booking';
      return 'occupied';
    }
    
    if (selectedDesk === desk.desk_id) return 'selected';
    
    return 'available';
  };

  const getDeskClasses = (status) => {
    const baseClass = 'desk-item';
    
    switch (status) {
      case 'available':
        return cn(baseClass, 'desk-available');
      case 'occupied':
        return cn(baseClass, 'desk-occupied');
      case 'unavailable':
        return cn(baseClass, 'desk-unavailable');
      case 'selected':
        return cn(baseClass, 'desk-selected');
      case 'my-booking':
        return cn(baseClass, 'desk-my-booking');
      default:
        return baseClass;
    }
  };

  const canClickDesk = (status) => {
    return status === 'available' || status === 'selected';
  };

  const handleDeskClick = (desk) => {
    const status = getDeskStatus(desk);
    if (canClickDesk(status) && selectedDates.length > 0) {
      onDeskClick(desk.desk_id);
    }
  };

  const getDesk = (id) => desks.find(d => d.desk_id === id) || { desk_id: id, is_available: true };

  const renderDesk = (desk) => {
    const status = getDeskStatus(desk);
    const booking = getBookingForDesk(desk.desk_id);
    const canClick = canClickDesk(status) && selectedDates.length > 0;

    return (
      <div
        key={desk.desk_id}
        data-testid={`desk-${desk.desk_id}`}
        className={cn(
          getDeskClasses(status),
          !canClick && status === 'available' && 'opacity-60'
        )}
        onClick={() => handleDeskClick(desk)}
        role="button"
        tabIndex={canClick ? 0 : -1}
        aria-label={`${t('desk')} ${desk.desk_id} - ${status}`}
      >
        {desk.has_monitor && (
          <Monitor className="w-3 h-3 md:w-4 md:h-4 absolute top-1 right-1 opacity-70" />
        )}
        <span className="text-2xl font-extrabold">{desk.desk_id}</span>
        
        {status === 'unavailable' && (
          <span className="text-xs mt-1 hidden md:inline">{t('notActive')}</span>
        )}
        
        {status === 'occupied' && (
          <span className="text-xs mt-1 hidden md:inline">
            {isAdmin && booking?.user_name ? booking.user_name : t('occupied')}
          </span>
        )}
        
        {status === 'my-booking' && (
          <span className="text-xs mt-1 hidden md:inline">{t('yourBooking')}</span>
        )}
        
        {status === 'available' && selectedDates.length === 0 && (
          <span className="text-xs mt-1 opacity-70"></span>
        )}
        
        {status === 'selected' && (
          <span className="text-xs mt-1"></span>
        )}
      </div>
    );
  };

  const renderVerticalPair = (topDeskId, bottomDeskId) => (
    <div className="flex flex-col gap-2">
      {renderDesk(getDesk(topDeskId))}
      {renderDesk(getDesk(bottomDeskId))}
    </div>
  );

  return (
    <div className="space-y-4">
      {/* Legend */}
      <div className="flex flex-wrap gap-2 md:gap-4 justify-center text-xs md:text-sm mb-4 md:mb-6">
        <div className="flex items-center gap-1 md:gap-2">
          <div className="w-3 h-3 md:w-4 md:h-4 rounded bg-emerald-400" />
          <span className="text-slate-600">{t('available')}</span>
        </div>
        <div className="flex items-center gap-1 md:gap-2">
          <div className="w-3 h-3 md:w-4 md:h-4 rounded bg-rose-400" />
          <span className="text-slate-600">{t('occupied')}</span>
        </div>
        <div className="flex items-center gap-1 md:gap-2">
          <div className="w-3 h-3 md:w-4 md:h-4 rounded bg-blue-500" />
          <span className="text-slate-600">{t('yourBooking')}</span>
        </div>
        <div className="flex items-center gap-1 md:gap-2">
          <div className="w-3 h-3 md:w-4 md:h-4 rounded bg-indigo-500" />
          <span className="text-slate-600">{t('selected')}</span>
        </div>
        <div className="flex items-center gap-1 md:gap-2">
          <div className="w-3 h-3 md:w-4 md:h-4 rounded bg-slate-200" style={{backgroundImage: 'repeating-linear-gradient(45deg, transparent, transparent 2px, rgba(148, 163, 184, 0.3) 2px, rgba(148, 163, 184, 0.3) 4px)'}} />
          <span className="text-slate-600">{t('notActive')}</span>
        </div>
        <div className="flex items-center gap-1 md:gap-2">
          <Monitor className="w-3 h-3 md:w-4 md:h-4 text-slate-500" />
          <span className="text-slate-600">{t('monitor')}</span>
        </div>
      </div>

      {/* Floor plan */}
      <div className="bg-slate-100 rounded-2xl md:rounded-3xl p-4 md:p-8 relative overflow-hidden">
        <div className="hidden md:flex absolute left-2 bottom-8 bg-blue-500 text-white text-xs px-3 py-2 rounded-full font-bold writing-mode-vertical">
          {t('entrance')}
        </div>
        
        <div className="hidden md:flex absolute right-2 top-4 bottom-4 bg-sky-400 text-white text-xs px-2 rounded-full font-bold items-center justify-center writing-mode-vertical">
          {t('windows')}
        </div>

        <div className="flex justify-center gap-2 sm:gap-4 md:gap-8 mb-4 md:mb-8">
          {renderVerticalPair(1, 2)}
          {renderVerticalPair(3, 4)}
          {renderVerticalPair(5, 6)}
        </div>

        <div className="text-center text-slate-400 text-xs md:text-sm mb-4 md:mb-8 border-t border-b border-dashed border-slate-300 py-1 md:py-2">
          {t('corridor')}
        </div>

        <div className="flex justify-center gap-2 sm:gap-4 md:gap-8">
          {renderVerticalPair(7, 8)}
          {renderVerticalPair(9, 10)}
          {renderVerticalPair(11, 12)}
        </div>
      </div>
    </div>
  );
}
